import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MenuItem, MessageService } from 'primeng/api';
import { AuthGateway } from 'src/app/application/gateway/AuthGateway';
import { CountryGateway } from 'src/app/application/gateway/CountryGateway';
import { ExportOfferGateway } from 'src/app/application/gateway/ExportOfferGateway';
import { FridgeStorageGateway } from 'src/app/application/gateway/FridgeStorageGateway';
import { ProductCertificateTypeGateway } from 'src/app/application/gateway/ProductCertificateTypeGateway';
import { SpeciesGateway } from 'src/app/application/gateway/SpeciesGateway';
import { VarietyGateway } from 'src/app/application/gateway/VarietyGateway';
import { Country } from 'src/app/domain/models/Country';
import { ExportOffer, OfferPicture } from 'src/app/domain/models/ExportOffer';
import { FridgeStorage } from 'src/app/domain/models/FridgeStorage';
import { ProductCertificateType } from 'src/app/domain/models/ProductCertificateType';
import { Profile } from 'src/app/domain/models/Profile';
import { Species, SpeciesSize } from 'src/app/domain/models/Species';
import { BasicUser } from 'src/app/domain/models/User';
import { Variety } from 'src/app/domain/models/Variety';
import { Routes } from 'src/app/infrastructure/routes';
import { ExportOfferUtils } from 'src/app/ui/util/export-offer-utils';
import { Lang } from 'src/app/ui/util/langs';
import { environment } from 'src/environments/environment';


interface Picture {
  picture: OfferPicture
  file: any
}

@Component({
  selector: 'app-admin-edit-export-offer',
  templateUrl: './admin-edit-export-offer.component.html',
  styleUrls: ['./admin-edit-export-offer.component.css'],

})
export class AdminEditExportOfferComponent implements OnInit {

  breadcrumbMenuItems: MenuItem[] = []
  breadcrumbHome: MenuItem = {}

  exportOfferId: number | undefined
  exportOffer: ExportOffer | undefined

  species: Species[] = []
  varieties: Variety[] = []
  colors: string[] = []
  sizes: SpeciesSize[] = []
  qualityControlOptions: any[] = []
  certificateTypes: ProductCertificateType[] = []
  boxTypes: any[] = []
  kilosPerBoxes: any[] = []
  basePallets: any[] = []
  selectedCertifications: ProductCertificateType[] = []
  fridgeStorages: any[] = []
  pictures: Picture[] = []
  marksOptions: any[] = []
  removedPictures: number[] = []
  countries: Country[] = []

  incotermsOptions: any[] = ExportOfferUtils.getIncotermsOptions()

  loading: boolean = false
  submit: boolean = false
  isSaveColdStorage: boolean = false

  profile: Profile | undefined

  formGroup: FormGroup = this._formBuilder.group({
    speciesId: ['', Validators.required],
    varietyId: ['', Validators.required],
    palletQuantity: [null, Validators.required],
    fridgeStorageId: ['', Validators.required],
    listPrice: [null, Validators.required],
    caseQuantityPerPallet: [null, Validators.required],
    incoterm: ['', Validators.required],
    color: [''],
    speciesSize: [''],
    harvestDate: [null, Validators.required],
    readyToLoadDate: [null],
    shelfLife: [null],
    boxType: ['', Validators.required],
    kilosPerBox: [''],
    netKilos: ['', Validators.required],
    grossKilos: ['', Validators.required],
    isQualityControl: [false],
    qualityControlDate: [null],
    basePallets: [''],
    marks: [false],
    marketCountry1Id: ['', Validators.required],
    marketCountry2Id: [''],
    marketCountry3Id: ['']
  })

  loadFridgestorageDialogVisible: boolean = false
  saleType: string = "";
  user: BasicUser | undefined

  constructor(private _formBuilder: FormBuilder,
    private _messageService: MessageService,
    private _router: Router,
    private _activatedRoute: ActivatedRoute,
    private _varietyService: VarietyGateway,
    private _speciesService: SpeciesGateway,
    private _fridgeStorageService: FridgeStorageGateway,
    private _exportOfferService: ExportOfferGateway,
    private _productCertificateTypeService: ProductCertificateTypeGateway,
    private _countryService: CountryGateway,
    public readonly translate: TranslateService,
    private readonly _authService: AuthGateway
    ){
      this._activatedRoute.params.subscribe(
        params => {
          this.exportOfferId = params['exportOfferId']
        }
      )

      if (this._router.getCurrentNavigation()?.extras?.state?.['saleType'] !== undefined) {
        this.saleType = this._router.getCurrentNavigation()?.extras?.state?.['saleType'];
      }
    }

    ngOnInit(): void {
      this.user = this._authService.getUser()
      this.loadProductCertificateTypes()
      this.loadExportOffer()
      this.loadBreadcrumb();

      this.colors = ExportOfferUtils.getColors(Lang.SPANISH)
      this.boxTypes = ExportOfferUtils.getBoxTypes(Lang.SPANISH)
      this.kilosPerBoxes = ExportOfferUtils.getKilosPerBox()
      this.basePallets = ExportOfferUtils.getBasePallets(Lang.SPANISH)
      this.qualityControlOptions = [{label: 'Si', value: true}, {label: 'No', value: false}]
      this.marksOptions = [{label: 'Si', value: true}, {label: 'No', value: false}]
    }

    loadBreadcrumb() {
      this.translate.get([
        'AdminExportOffer.PageTitle',
        'AdminExportOffer.Detail.OfferDetailLabel',
      ]).subscribe({
        next: (tranlations) => {
          const redirect = this.saleType === "program" ? Routes.adminProgramExportOfferPage : Routes.adminMarketplaceExportOfferPage
          this.breadcrumbHome = {
            label: tranlations['AdminExportOffer.PageTitle'],
            routerLink: ['/' + redirect]
          }
          this.breadcrumbMenuItems = [{
            label: tranlations['AdminExportOffer.Detail.OfferDetailLabel']
          }]
        },
        error: () => {}
      })
    }

    loadExportOffer() {
      if(this.exportOfferId) {
        this._exportOfferService.getById(this.exportOfferId).subscribe({
          next: (v) => {
            this.exportOffer = v
            this.loadForm()
          },
          error: (e) => {
            this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudo recuperar el perfil.' });
          }
        })
      }
    }

    loadForm() {
      if(this.exportOffer) {
        this.loadSpecies()
        this.formGroup.controls['speciesId'].setValue(this.exportOffer.variety.speciesId)
        this.loadVarieties(true)
        this.loadSpeciesSizes(true)
        this.formGroup.controls['palletQuantity'].setValue(this.exportOffer.palletQuantity)
        this.formGroup.controls['caseQuantityPerPallet'].setValue(this.exportOffer.caseQuantityPerPallet)
        this.formGroup.controls['listPrice'].setValue(this.exportOffer.listPrice)
        this.loadFridgeStorages(true)
        this.formGroup.controls['color'].setValue(this.exportOffer.color)

        if (this.exportOffer?.harvestTypeId === 2 || this.exportOffer?.harvestTypeId === 3) {
          if (this.exportOffer?.harvestStartDate && this.exportOffer?.harvestEndDate) {
            this.formGroup.controls['harvestDate'].setValue([new Date(this.exportOffer?.harvestStartDate), new Date(this.exportOffer?.harvestEndDate)])
          }
        }
        else {
          this.formGroup.controls['harvestDate'].setValue(this.exportOffer.harvestDate ? new Date(this.exportOffer.harvestDate) : null)
        }

          this.formGroup.controls['readyToLoadDate'].setValue(this.exportOffer.readyToLoadDate ? new Date(this.exportOffer.readyToLoadDate) : null)
        this.formGroup.controls['shelfLife'].setValue(this.exportOffer.shelfLife)
        this.formGroup.controls['boxType'].setValue(this.exportOffer.boxType)
        this.formGroup.controls['kilosPerBox'].setValue(this.exportOffer.kilosPerBox)
        this.formGroup.controls['netKilos'].setValue(this.exportOffer.netKilos);
        this.formGroup.controls['grossKilos'].setValue(this.exportOffer.grossKilos);
        this.formGroup.controls['isQualityControl'].setValue(this.exportOffer.isQualityControl)
        this.formGroup.controls['qualityControlDate'].setValue(this.exportOffer.qualityControlDate ? new Date(this.exportOffer.qualityControlDate) : null)
        this.formGroup.controls['basePallets'].setValue(this.exportOffer.basePallets)
        this.formGroup.controls['marks'].setValue(this.exportOffer.marks)
        this.formGroup.controls['incoterm'].setValue(this.exportOffer.typeSale)
        this.loadCountries(true)

        this.exportOffer.offerPictures?.forEach(async p => {
          p.isNew = false
          this.pictures.push({picture: p, file: null})
        })

        //Copy new array
        this.selectedCertifications = []
        this.selectedCertifications = this.exportOffer.productCertificateTypes ? this.exportOffer.productCertificateTypes : []
      }
    }

    loadSpeciesParams() {
      this.loadVarieties(false)
      this.loadSpeciesSizes(false)
      this.loadCountries(false)
    }

    loadVarieties(setValue: boolean) {
      this.varieties = []
      this._varietyService.getAllBySpecies(this.formGroup.controls['speciesId'].value).subscribe({
        next: (v) => {
          this.varieties = v
          this.loading = false
          if(setValue && this.exportOffer && this.exportOffer.variety)
            this.formGroup.controls['varietyId'].setValue(this.exportOffer.variety.id)
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar las variedades.' })
        }
      })
    }

    loadSpeciesSizes(setValue: boolean) {
      this.sizes = []
      this._speciesService.getSpeciesSizes(this.formGroup.controls['speciesId'].value).subscribe({
        next: (v) => {
          this.sizes = v
          this.loading = false
          if(setValue && this.exportOffer && this.exportOffer.speciesSize)
            this.formGroup.controls['speciesSize'].setValue(this.exportOffer.speciesSize)
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar los calibres.' })
        }
      })
    }

    loadSpecies() {
      this._speciesService.getAll().subscribe({
        next: (v) => {
          this.species = v
          this.loading = false
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar las especies.' })
        }
      })
    }

    loadProductCertificateTypes() {
      this._productCertificateTypeService.getAll().subscribe({
        next: (v) => {
          this.certificateTypes = v
          this.loading = false
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar los tipos de certificados.' })
        }
      })
    }

    loadFridgeStorages(setValue: boolean) {
      this.fridgeStorages = []
      this._fridgeStorageService.getAllAvailableAdmin(this.exportOffer?.exporter.contactEmail ?? '').subscribe({
        next: (v) => {
          this.fridgeStorages = v
          this.fridgeStorages.push({
            id: 0,
            name: this.translate.instant('MessageService.FridgeStorage.CreateOptionLabel') + '...'
          })
          this.loading = false
          if(setValue && this.exportOffer?.fridgeStorage !== null) {
            this.formGroup.controls['fridgeStorageId'].setValue(this.exportOffer?.fridgeStorageId)
          }
        },
        error: (e) => {
          this.loading = false
          this._messageService.add({
            severity: 'error',
            summary: 'Acción fallida',
            detail: 'No se pudieron recuperar los frigoríficos.'
          })
        }
      })
    }

    loadCountries(setValue: boolean) {
      this.countries = []
      this._countryService.getAll().subscribe({
        next: (v) => {
          this.countries = v
          this.loading = false
          if(setValue && this.exportOffer) {
            this.formGroup.controls['marketCountry1Id'].setValue(this.exportOffer.marketCountryFirstId)
            this.formGroup.controls['marketCountry2Id'].setValue(this.exportOffer.marketCountrySecondId)
            this.formGroup.controls['marketCountry3Id'].setValue(this.exportOffer.marketCountryThirdId)
          }
        },
        error: (e) => {
          console.log(e)
          this.loading = false
          this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudieron recuperar los calibres.' })
        },
        complete: () => console.info('Species Sizes query successfully')
      })
    }

    getImageUrl(picture: Picture): any {
      if(picture) {
        if(!picture.picture.isNew && picture.picture.url) {
          return environment.apiUrl + picture.picture.url
        }
        else {
          return picture.file.objectURL
        }
      }
      return ''
    }

    onChangeColdStorage() {
      const selected = this.formGroup.controls['fridgeStorageId'].value
      if (selected === 0) {
        this.loadFridgestorageDialogVisible = true;
        this.formGroup.controls['fridgeStorageId'].setValue('')
      }
    }

    onCloseColdStorageDialog(isReloadColdStorages?: boolean) {
      if (isReloadColdStorages === true) {
        this.loadFridgeStorages(true);
        this._messageService.add({ severity: 'success',
          summary: this.translate.instant('MessageService.Summary.SuccessAction'),
          detail: this.translate.instant('MessageService.FridgeStorage.CreateSuccess')
        })
      }
      this.loadFridgestorageDialogVisible = false;
    }

    onSaveColdStorage() {
      this.isSaveColdStorage = !this.isSaveColdStorage;
    }

    save() {
      this.loading = true
      this.submit = true
      if(this.formGroup.valid && this.exportOffer) {
        this.exportOffer.varietyId = this.formGroup.controls['varietyId'].value
        this.exportOffer.palletQuantity = this.formGroup.controls['palletQuantity'].value
        this.exportOffer.fridgeStorageId = this.formGroup.controls['fridgeStorageId'].value
        this.exportOffer.availablePallets = this.formGroup.controls['palletQuantity'].value
        this.exportOffer.color = this.formGroup.controls['color'].value
        this.exportOffer.harvestDate = this.formGroup.controls['harvestDate'].value
        this.exportOffer.readyToLoadDate = this.formGroup.controls['readyToLoadDate'].value
        this.exportOffer.boxType = this.formGroup.controls['boxType'].value
        this.exportOffer.kilosPerBox = this.formGroup.controls['kilosPerBox'].value
        this.exportOffer.netKilos = this.formGroup.controls['netKilos'].value
        this.exportOffer.grossKilos = this.formGroup.controls['grossKilos'].value
        this.exportOffer.caseQuantityPerPallet = this.formGroup.controls['caseQuantityPerPallet'].value
        this.exportOffer.isQualityControl = this.formGroup.controls['isQualityControl'].value
        this.exportOffer.shelfLife = this.formGroup.controls['shelfLife'].value
        this.exportOffer.speciesSize = this.formGroup.controls['speciesSize'].value
        this.exportOffer.listPrice = this.formGroup.controls['listPrice'].value
        this.exportOffer.qualityControlDate = this.formGroup.controls['qualityControlDate'].value
        this.exportOffer.basePallets = this.formGroup.controls['basePallets'].value
        this.exportOffer.marks = this.formGroup.controls['marks'].value
        this.exportOffer.typeSale = this.formGroup.controls['incoterm'].value
        this.exportOffer.marketCountryFirstId = this.formGroup.controls['marketCountry1Id'].value
        this.exportOffer.marketCountrySecondId = this.formGroup.controls['marketCountry2Id'].value
        this.exportOffer.marketCountryThirdId = this.formGroup.controls['marketCountry3Id'].value

        //Set the removed pictures
        this.exportOffer.pictures = []
        if(this.pictures) {
          this.pictures.forEach(p => {
            if (p.picture.isNew)
              this.exportOffer?.pictures.push(p.file)
          })
        }
        this.exportOffer.removedPictures = this.removedPictures

        //Set the selected certificates
        this.exportOffer.certificates = this.selectedCertifications

        //Filter and set the removed product certificates
        let removedCerts: ProductCertificateType[] = []
        if(this.selectedCertifications && this.exportOffer.productCertificateTypes) {
          this.exportOffer.productCertificateTypes.forEach(c => {
            const keep = this.selectedCertifications.find(s => s.id === c.id)
            if(!keep) {
              removedCerts.push(c)
            }
          })
        }
        this.exportOffer.removedCertificates = removedCerts

        this._exportOfferService.updateAdmin(this.exportOffer).subscribe({
          next: (v) => {
            this.exportOffer = v
            //this.loadForm()
            this._messageService.add({ severity: 'success', summary: 'Acción exitosa', detail: 'La oferta de exportación fue actualizada con éxito.' })
            this.submit = false
            this.loading = false
          },
          error: (e) => {
            this.loading = false
            console.log(e)
            this._messageService.add({ severity: 'error', summary: 'Acción fallida', detail: 'No se pudo actualizar la oferta de exportación. Inténtelo de nuevo.' });
          },
          complete: () => console.info('Export Offer update successfully')
        })

      }
      else {
        this.loading = false
        this._messageService.add({
          severity: 'error',
          summary: this.translate.instant('AdminExportOffer.Messages.FailedActionTitle'),
          detail: this.translate.instant('AdminExportOffer.Messages.FailedActionDetail')
        });
      }
    }

    getErrorMessage(fieldName: string): string {
      if (this.formGroup.controls[fieldName].hasError('required')) {
        return 'Este campo es requerido.';
      }
      if (this.formGroup.controls[fieldName].hasError('email')) {
        return 'Formato de correo electrónico incorrecto.';
      }
      return '';
    }

    goToExportOffersPage() {
      if (this.saleType === "program") {
        this._router.navigate(['/' + Routes.adminProgramExportOfferPage])
      }
      else {
        this._router.navigate(['/' + Routes.adminMarketplaceExportOfferPage])
      }
    }

    onRemoveImage(index: number): void {
      if(this.exportOffer && this.pictures[index]) {
        this.removedPictures.push(this.pictures[index].picture.id)
        this.pictures.splice(index, 1)
      }
      console.log(this.removedPictures)
    }

    getImageName(picture: Picture): string {
      let fileName = ''
      if(!picture.picture.isNew && picture.picture.url) {
        const urlParts: string[] = picture.picture.url.split('/')
        fileName = urlParts[urlParts.length-1]
      }
      else {
        return picture.file.name
      }
      return fileName
    }

    onSelectImage(event: any, uploader: any): void {
      this.pictures.push(
        {
          picture: {id: 0,
                    url: uploader.files[0].objectURL.changingThisBreaksApplicationSecurity,
                    isNew: true},
          file: uploader.files[0]
        })
      uploader.clear()
    }
}
